/**
 * LifecyclePill — the workshop publication-lifecycle pill (BON-117 / BON-202 /
 * BON-197 R1). Amber "• Draft" / green "• Active" / red "• Cancelled" / blue
 * "• Disabled". Active reuses the shared StatusPill `active` tone (green dot);
 * the others render dot pills in the same shape (no matching tone exists on
 * StatusPill).
 *
 * When `startsAt` is supplied (the detail-page header, BON-197 R1) the pill
 * delegates to the shared Axis-A resolver so an active + PAST workshop reads
 * "Completed" instead of "Active" — the single lifecycle-status badge on that
 * page. Callers that don't care about the past/future axis omit `startsAt` and
 * get the legacy lifecycle-only rendering.
 */
import type { WorkshopLifecycle } from '../../../../api/workshops';
import { DANGER_RED, DotPill, StatusPill } from '../../primitives';
import { WorkshopStatusPill } from '../../venues/detail/workshopStatus';

export function LifecyclePill({
  lifecycle,
  startsAt,
}: {
  lifecycle: WorkshopLifecycle;
  /** ISO datetime. When present, an active+past workshop derives "Completed". */
  startsAt?: string;
}) {
  // Axis A (lifecycle status, past/future-aware): the shared resolver derives
  // Active vs Completed and renders Draft/Cancelled identically to below.
  if (startsAt !== undefined) {
    return <WorkshopStatusPill workshop={{ lifecycle, startsAt }} />;
  }
  if (lifecycle === 'active') {
    return <StatusPill tone="active" label="Active" />;
  }
  if (lifecycle === 'cancelled') {
    return <DotPill label="Cancelled" color={DANGER_RED} background="rgba(192,57,43,.12)" />;
  }
  if (lifecycle === 'disabled') {
    return <DotPill label="Disabled" color="var(--nb-info)" background="var(--nb-info-soft)" />;
  }
  return <DotPill label="Draft" color="var(--nb-warn)" background="var(--nb-warn-soft)" />;
}
